import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider as TanStackRouterProvider, createBrowserHistory, createRouter } from '@tanstack/react-router';
import { APIError, APIErrorType, useAuth } from '@vizit/auth';
import { useAnalytics } from '../../analytics';
import { routeTree } from '../../routeTree.gen';
import type { RouteContext } from './types';

// Router Context
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        if (
          error instanceof APIError &&
          (error.type === APIErrorType.NotAuthenticated || error.type === APIErrorType.UnprocessableContent)
        ) {
          Sentry.addBreadcrumb({
            message: 'Skipping query retry due to auth error',
            level: 'info',
            data: { error_type: error.type },
          });
          return false;
        }

        if (error instanceof APIError && error.status === 404) {
          Sentry.addBreadcrumb({
            message: 'Skipping query retry due to 404 error',
            level: 'info',
            data: { error_status: error.status },
          });
          return false;
        }

        return failureCount <= 3; // Retry up to 3 times
      },
      refetchOnWindowFocus: (error) => {
        if (
          error instanceof APIError &&
          error.status === 404 &&
          (error.type === APIErrorType.NotAuthenticated || error.type === APIErrorType.UnprocessableContent)
        ) {
          return false;
        }

        return true;
      },
    },
  },
});

const context: RouteContext = { queryClient };

// Router
const history = createBrowserHistory();

const router = createRouter({
  context,
  defaultPreload: 'intent',
  history,
  notFoundMode: 'root',
  routeTree,
  scrollRestoration: true,
});

/**
 * The RouterProvider component is responsible for providing the router context and
 * history (state) to the application. Use `useRouteContext` to access the router context,
 * like `queryClient`, `auth`, etc.
 *
 * @returns The RouterProvider component.
 */
function RouterProvider() {
  const auth = useAuth();
  const analytics = useAnalytics();

  return (
    <QueryClientProvider client={queryClient}>
      <TanStackRouterProvider router={router} context={{ ...context, auth, analytics }} />
    </QueryClientProvider>
  );
}

export { RouterProvider, router };
